import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import uniq from "lodash/uniq";

import { usePreviousRouteContext } from "@stadium/hooks/lib/use-previous-route";

import {
  StyledLink,
  StyledButton,
  type StyledLinkProps,
  type StyledButtonProps,
} from "@stadium/ui/src/components/button/styled-button";

import {
  useJuneSoEventTracking,
  TrackEventArgumentsTypes,
} from "@stadium/services/june-analytics/hooks/use-june-so-event-tracking";

import { getRoutesWithWorkspace } from "@stadium/helpers/routes";

import { DEFAULT_SERVICES } from "@constants/june-so";
import { LOGIN_CALLBACK_ROUTE, LOGOUT_CALLBACK_ROUTE } from "@stadium/services/auth0";
import { DASHBOARD_URL } from "@stadium/helpers/app-routes";

import styles from "./main.module.scss";

export {
  WorkspaceLinkButton as LinkButton,
  WorkspaceExternalLinkButton as ExternalLinkButton,
} from "@stadium/shared/src/components/shared/button";

type JuneSoPropsTypes = {
  services?: readonly string[];
} & TrackEventArgumentsTypes;

type ButtonProps = { juneSoProps?: JuneSoPropsTypes } & StyledButtonProps;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ juneSoProps, ...buttonProps }: ButtonProps, ref) => {
    const { services = DEFAULT_SERVICES, ...juneSoEventProps } = juneSoProps || {};

    const { trackEvent } = useJuneSoEventTracking({ services });

    const onClick = (args: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      buttonProps.onClick?.(args);
      trackEvent({ ...juneSoEventProps });
    };

    return (
      <StyledButton
        displayChildren
        styles={styles}
        disableOnLoad
        {...buttonProps}
        onClick={onClick}
        ref={ref}
      />
    );
  },
);

const BASE_BACK_BUTTON_SKIPPED_ROUTES = [LOGIN_CALLBACK_ROUTE, LOGOUT_CALLBACK_ROUTE];

export const defaultBackButtonAction: (
  e: Parameters<React.MouseEventHandler<HTMLAnchorElement>>[0],
) => boolean = (e) => {
  if (window.opener) {
    e.preventDefault();
    window.opener.focus();
    window.close();
    return true;
  }
  return false;
};

type BackButtonProps = StyledLinkProps & {
  baseClassName?: string;
  checkOpener?: boolean;
  skipRoutes?: string[];
  baseSkipRoutes?: string[];
  allowedRoutes?: string[];
  fallbackRoute?: string;
  appendQueryParams?: boolean;
  forceUseHref?: boolean;
};

export const BackButton = forwardRef<
  HTMLAnchorElement & { triggerBackAction: () => void },
  BackButtonProps
>(
  (
    {
      children,
      className = "",
      baseClassName = "",
      checkOpener = true,
      onClick,
      skipRoutes,
      isLoading,
      href: fallbackRoute = DASHBOARD_URL,
      baseSkipRoutes = BASE_BACK_BUTTON_SKIPPED_ROUTES,
      allowedRoutes,
      appendQueryParams,
      forceUseHref = false,
      ...props
    },
    ref,
  ) => {
    const { history, getPreviousUrl } = usePreviousRouteContext();
    const { replace, push } = useRouter();
    const anchorRef = useRef<HTMLAnchorElement>(null);

    const href = useMemo(() => {
      if (forceUseHref) {
        return fallbackRoute;
      }
      const skippedRoutes = skipRoutes?.length
        ? [...skipRoutes, ...baseSkipRoutes]
        : baseSkipRoutes;

      const modifiedSkippedRoutes = uniq([
        ...skippedRoutes,
        ...getRoutesWithWorkspace(skippedRoutes),
      ]);
      const modifiedAllowedRoutes = allowedRoutes?.length
        ? uniq([...allowedRoutes, ...getRoutesWithWorkspace(allowedRoutes)])
        : [];

      const prevRoute = getPreviousUrl({
        skippedRoutes: modifiedSkippedRoutes,
        allowedRoutes: modifiedAllowedRoutes,
      });

      if (!prevRoute) {
        return fallbackRoute;
      }
      if (appendQueryParams) {
        return prevRoute.href;
      }
      return prevRoute.pathname;
    }, [
      forceUseHref,
      skipRoutes,
      baseSkipRoutes,
      getPreviousUrl,
      appendQueryParams,
      fallbackRoute,
    ]);

    const defaultBackAction = useCallback(
      (e?: React.MouseEvent<HTMLAnchorElement>) => {
        if (checkOpener && history.length === 0 && defaultBackButtonAction(e)) {
          return true;
        }

        onClick?.(e);

        if (href != fallbackRoute) {
          e?.preventDefault();
          replace(href);
          return true;
        }

        return false;
      },
      [checkOpener, history.length, href, onClick, replace, fallbackRoute],
    );

    useImperativeHandle(
      ref,
      () => ({
        ...anchorRef.current,
        triggerBackAction: () => {
          if (!defaultBackAction()) {
            push(href);
          }
        },
        defaultBackAction,
      }),
      [defaultBackAction, href, push],
    );

    return (
      <StyledLink
        href={href}
        className={`${className} ${baseClassName}`}
        onClick={defaultBackAction}
        ref={anchorRef}
        styles={styles}
        disabled={isLoading}
        {...props}
      >
        {children ?? "BACK"}
      </StyledLink>
    );
  },
);

export default Button;
